import { useContext, useMemo } from 'react';

import type { ProjectContextValue, UserContextValue } from '@core/context';
import { ProjectContext, UserContext } from '@core/context';

/**
 * Parses user and project contexts to return a more convenient set of booleans
 * that can be used to determine what type of user is currently here.
 * @example
 * ```ts
 * const { isLoggedIn, isAdminUser, isGroupAdminUser } = useUserPermissions();
 * ```
 */
const useUserPermissions = () => {
  const user = (useContext(UserContext) as unknown as UserContextValue) || {};
  // @ts-expect-error Property 'is_god' does not exist, Property 'loggedIn' does
  // not exist, Property 'isEndUser' does not exist
  const { permissions = [], _id: userId, is_god: isGod, loggedIn: isLoggedIn, isEndUser } = user;
  const { project } = (useContext(ProjectContext) as ProjectContextValue) || {};
  const parentId = project.parent?._id?.toString();
  const childId = project._id?.toString();

  // If a parent exists, also use its permissions; otherwise, just use the active project's permissions
  const relevantPerms = useMemo(
    () =>
      permissions.filter(perm =>
        parentId
          ? perm.project?.toString() === childId || perm.project?.toString() === parentId
          : perm.project?.toString() === childId,
      ),
    [permissions, parentId, childId],
  );

  // If user is an admin on this project or its parent then we consider them an admin
  const isAdminUser = useMemo(
    () => !!isGod || relevantPerms.some(perm => perm.userType === 'admin'),
    [isGod, relevantPerms],
  );

  // If user is an editor on this project or its parent then we consider them an editor
  const isEditorUser = useMemo(() => relevantPerms.some(perm => perm.userType === 'editor'), [relevantPerms]);

  // If user is an owner or admin of the group that this project belongs to, then we consider them a group admin
  const isGroupAdminUser = useMemo(() => {
    if (!project.parent) return false;

    // If project owner, they should be able to setup what they'd like at the group level
    if (isGod || project.parent.owner?.id === userId) return true;

    // Else check if current user has group-level admin permissions
    return !!permissions.find(
      p => p?.project?.toString() === project.parent?._id?.toString() && p.userType === 'admin',
    );
  }, [isGod, project, permissions, userId]);

  const isOwner = useMemo(
    () => (project.owner?.id === user?.user?.teammateUserId || project.owner?.id === userId) && !!project.owner?.id,
    [project.owner?.id, user?.user?.teammateUserId, userId],
  );

  /** Whether the current user is a team viewer. */
  const isViewerUser = useMemo(() => {
    // Team viewers only exist in projects that are part of a group.
    if (!project.parent) return false;
    // Team viewers can never be group admins.
    if (isGroupAdminUser) return false;
    // Team viewers can never be admin.
    if (isAdminUser) return false;
    // Team viewers can never be editors.
    if (isEditorUser) return false;
    // Team viewers can never be end users.
    if (isEndUser) return false;

    return relevantPerms.some(perm => perm.userType === 'readonly');
  }, [isAdminUser, isEditorUser, isEndUser, isGroupAdminUser, project.parent, relevantPerms]);

  // If user has any permissions, they are considered a member
  // This includes users who are viewers as well as admins, editors, or owners
  const isMember = !!relevantPerms.length;

  return {
    /**
     * Current user is an admin for the current project or its parent group.
     */
    isAdminUser,
    /** Current user is a team editor for the current project. */
    isEditorUser,
    /**
     * Current user is an end user who is purely consuming content. This is not
     * a team member (admin, editor, viewer) and has no special permissions.
     */
    isEndUser,
    /** Current user is an owner or admin of the parent group project. */
    isGroupAdminUser,
    /** Current user is logged in. */
    isLoggedIn,
    isMember,
    /** Current user is the owner of the current project. */
    isOwner,
    /**
     * Current user is a team viewer. Note that this is different from an end
     * user. Viewers are members of the project whose permissions allow them an
     * internal view of the project, but cannot make changes. An end user is
     * someone outside of the team who is consuming the content.
     */
    isViewerUser,
  };
};

export default useUserPermissions;
